/**
 * The rules presets page (place manager, unit 11), read-only: every preset's
 * settings side by side ({@link presetGridOf}), and where each one is used
 * ({@link whereUsedOf}). Pure: the reads live in `presets-api.ts` and
 * `place-schedule-api.ts`, so the grid and the reverse index are
 * unit-testable without a network.
 *
 * No backend read goes from a preset to what uses it, so the index is put
 * together here, each link matched the way the backend makes it: a room by
 * object (`Gathering.settings`, matched on id), a place's default for new
 * tables by id, a template by slug, exactly as stored (its JSON's
 * `gatheringSettingsTagSlug` for the tables, `lobbyGatheringSettingsTagSlug`
 * for the lobby; read by `templateKeysOf`; the backend looks a slug up as is,
 * and slugs are unique), a repeat through its template. Coming events are not
 * read here (that would mean every place's seasons): see each place's
 * Schedule.
 *
 * Archived presets are not listed (the presets read leaves them out), so a
 * room, default or template still pointing at one counts as "not listed
 * here", never as broken.
 *
 * The page describes what is; it never rates, recommends or ranks a preset.
 */
import { plural } from "@/lib/utils";
import type { PlaceWithRoomsData, PresetData, PresetRoles } from "@/data/presets-api";
import type { EventTemplateData, ScheduledEventData } from "@/data/place-schedule-api";
import { templateKeysOf, type TemplateKeys } from "@/features/places/schedule";

/** A roles action (one row of the grid). */
export type RoleAction = keyof PresetRoles;

/** Every roles action, in the schema's order (`GatheringSettingsRoles`); true
 *  where the schema marks the action deprecated. `presets.test.ts` ties this
 *  to the schema snapshot. */
export const ROLE_ACTIONS = {
  adjustGameSettings: false,
  advanceEventToFinalResults: false,
  changeGame: false,
  createSubgathering: false,
  doGameAdmin: false,
  endEvent: false,
  endGame: false,
  endGathering: false,
  inviteToCall: false,
  inviteToSeat: false,
  inviteToStage: false,
  kickMember: false,
  modifyGatheringSettings: false,
  muteUserGlobally: false,
  removeFromSeatAndCall: false,
  removeFromStage: false,
  selfJoinCall: false,
  selfJoinSeat: false,
  selfJoinStage: false,
  startGame: false,
} as const satisfies Record<RoleAction, boolean>;

/** The legend under the grid. */
export const ROLES_LEGEND = "Role words as the backend stores them.";

/** What an empty roles cell says. */
export const NOBODY = "nobody";

/** A backend name in plain words: "adjustGameSettings" → "adjust game
 *  settings", "FRIEND_VISIBLE" → "friend visible". Spacing and case only. */
export function wordsOf(name: string): string {
  return name
    .replace(/_/g, " ")
    .replace(/([a-z0-9])([A-Z])/g, "$1 $2")
    .toLowerCase();
}

/** One column: a preset, as its head shows it. */
export interface PresetColumn {
  id: string;
  name: string;
  /** Null when the preset has none (then no template can name it). */
  slug: string | null;
  /** Null when the preset has no icon. */
  iconUrl: string | null;
}

/** One cell: plain text, or a roles list (empty: {@link NOBODY}). */
export type GridCell = { kind: "text"; text: string } | { kind: "roles"; roles: readonly string[] };

/** One row: a setting, or a roles action (`deprecated` as the schema says). */
export interface GridRow {
  key: string;
  label: string;
  kind: "setting" | "roles";
  deprecated: boolean;
  /** One per column, in column order. */
  cells: GridCell[];
}

export interface PresetGrid {
  columns: PresetColumn[];
  rows: GridRow[];
}

/** A preset as its column head shows it. */
export function columnOf(p: PresetData): PresetColumn {
  return { id: p.id, name: p.name, slug: p.slug || null, iconUrl: p.iconUrl.trim() || null };
}

/** Presets in column order: by sort score (low first, as the backend sorts
 *  its tags), then by name. */
export function presetsInOrder(presets: readonly PresetData[]): PresetData[] {
  return [...presets].sort((a, b) => a.sortScore - b.sortScore || a.name.localeCompare(b.name));
}

const yesNo = (v: boolean) => (v ? "yes" : "no");
const cleanup = (on: boolean, seconds: number) => `${on ? "on" : "off"} · ${seconds.toLocaleString()} s`;

/** The setting rows, in order: label and how a preset's cell reads. */
const SETTINGS: { key: string; label: string; cell: (p: PresetData) => string }[] = [
  { key: "description", label: "Description", cell: (p) => p.description.trim() || "none" },
  { key: "sortScore", label: "Sort score", cell: (p) => String(p.sortScore) },
  { key: "visibility", label: "Visibility", cell: (p) => wordsOf(p.gatheringVisibility) },
  { key: "selectable", label: "Selectable", cell: (p) => yesNo(p.selectable) },
  { key: "official", label: "Official", cell: (p) => yesNo(p.state.official) },
  { key: "public", label: "Public", cell: (p) => yesNo(p.state.public) },
  {
    key: "idleRooms",
    label: "Idle room cleanup",
    cell: (p) => cleanup(p.idleGatheringCleanupEnabled, p.idleGatheringGracePeriodSeconds),
  },
  {
    key: "idlePlayers",
    label: "Idle player cleanup",
    cell: (p) => cleanup(p.idleUserCleanupEnabled, p.idleUserGracePeriodSeconds),
  },
];

/** The grid: one column per preset ({@link presetsInOrder}), one row per
 *  setting, then one per roles action in the schema's order, each cell the
 *  role words exactly as stored. */
export function presetGridOf(presets: readonly PresetData[]): PresetGrid {
  const ordered = presetsInOrder(presets);
  const columns = ordered.map(columnOf);
  const settings: GridRow[] = SETTINGS.map((s) => ({
    key: s.key,
    label: s.label,
    kind: "setting",
    deprecated: false,
    cells: ordered.map((p) => ({ kind: "text", text: s.cell(p) })),
  }));
  const roles: GridRow[] = (Object.keys(ROLE_ACTIONS) as RoleAction[]).map((action) => ({
    key: action,
    label: wordsOf(action),
    kind: "roles",
    deprecated: ROLE_ACTIONS[action],
    cells: ordered.map((p) => ({ kind: "roles", roles: p.roles[action] })),
  }));
  return { columns, rows: [...settings, ...roles] };
}

/** Which of a template's presets a use is: the tables' or the lobby's. */
export type PresetRole = "tables" | "lobby";

/** A room running a preset: a place's main room or one of its open tables. */
export interface RoomUse {
  placeId: string;
  place: string;
  room: "main room" | "table";
  /** Null when the room has no title. */
  title: string | null;
}

/** A place whose new tables start with a preset. */
export interface DefaultUse {
  placeId: string;
  place: string;
}

/** A template naming a preset (for its tables, its lobby, or both). */
export interface TemplateUse {
  id: string;
  name: string;
  as: PresetRole[];
}

/** A repeat whose template names a preset. */
export interface RepeatUse {
  id: string;
  name: string;
  template: string;
  as: PresetRole[];
}

/** A list as read, or `failed` when the read behind it failed. */
export type Uses<T> = T[] | "failed";

/** Where one preset is used, by kind. */
export interface PresetUses {
  rooms: Uses<RoomUse>;
  defaults: Uses<DefaultUse>;
  templates: Uses<TemplateUse>;
  repeats: Uses<RepeatUse>;
}

/** A slug that templates (or repeats, through theirs) name and no listed
 *  preset has (it may be an archived preset's). */
export interface UnknownSlug {
  slug: string;
  templates: string[];
  repeats: string[];
}

/** The reads where-used is put together from, each on its own (one failing
 *  leaves the others). */
export interface WhereUsedReads {
  places: PlaceWithRoomsData[] | "failed";
  templates: EventTemplateData[] | "failed";
  repeats: ScheduledEventData[] | "failed";
}

export interface WhereUsed {
  /** Each preset's uses, by preset id. */
  byPreset: Map<string, PresetUses>;
  unknownSlugs: UnknownSlug[];
  /** Open tables beyond what the places read lists (it lists 50 per place). */
  tablesNotListed: number;
  /** Rooms whose settings are not a listed preset (archived, or not a preset). */
  roomsOnOtherSettings: number;
  /** Places whose default for new tables is not a listed preset. */
  defaultsOnOtherSettings: number;
  /** Templates and repeats whose JSON did not read, so what they name is not known. */
  jsonUnread: number;
}

/** The list under `key`, made empty the first time it is asked for. */
function listOf<T>(map: Map<string, T[]>, key: string): T[] {
  let list = map.get(key);
  if (!list) map.set(key, (list = []));
  return list;
}

/** The presets a template's JSON names, with which of its presets each is. */
function namedSlugs(keys: TemplateKeys): [string, PresetRole][] {
  const named: [string, PresetRole][] = [];
  if (keys.preset) named.push([keys.preset, "tables"]);
  if (keys.lobbyPreset) named.push([keys.lobbyPreset, "lobby"]);
  return named;
}

const byName = <T extends { name: string }>(a: T, b: T) => a.name.localeCompare(b.name);

/** The reverse index: for each preset, the rooms, place defaults, templates
 *  and repeats using it (see the module doc for how each link is matched). */
export function whereUsedOf(presets: readonly PresetData[], reads: WhereUsedReads): WhereUsed {
  const ids = new Set(presets.map((p) => p.id));
  const bySlug = new Map(presets.filter((p) => p.slug).map((p) => [p.slug!, p.id]));

  const rooms = new Map<string, RoomUse[]>();
  const defaults = new Map<string, DefaultUse[]>();
  let tablesNotListed = 0;
  let roomsOnOtherSettings = 0;
  let defaultsOnOtherSettings = 0;
  if (reads.places !== "failed") {
    const places = [...reads.places].sort(byName);
    const addRoom = (settingsId: string | undefined, use: RoomUse) => {
      if (!settingsId) return;
      if (!ids.has(settingsId)) roomsOnOtherSettings++;
      else listOf(rooms, settingsId).push(use);
    };
    for (const place of places) {
      const main = place.rootGathering;
      if (main) addRoom(main.settings?.id, { placeId: place.id, place: place.name, room: "main room", title: main.title });
      for (const table of place.subGatherings.nodes) {
        addRoom(table.settings?.id, { placeId: place.id, place: place.name, room: "table", title: table.title });
      }
      tablesNotListed += Math.max(0, place.subGatherings.totalCount - place.subGatherings.nodes.length);
      const preset = place.defaultGatheringSettingsForSubGatherings?.id;
      if (!preset) continue;
      if (!ids.has(preset)) defaultsOnOtherSettings++;
      else listOf(defaults, preset).push({ placeId: place.id, place: place.name });
    }
  }

  const unknown = new Map<string, UnknownSlug>();
  const unknownOf = (slug: string) => {
    const u = unknown.get(slug) ?? { slug, templates: [], repeats: [] };
    unknown.set(slug, u);
    return u;
  };
  let jsonUnread = 0;
  /** Each preset an entry names (by its template's JSON), with which of its
   *  presets each is; the slugs no preset has go to `unknown`. */
  const presetsNamedBy = (templateJson: string, name: string, kind: "templates" | "repeats") => {
    const keys = templateKeysOf(templateJson);
    const found = new Map<string, PresetRole[]>();
    if (!keys) {
      jsonUnread++;
      return found;
    }
    for (const [slug, as] of namedSlugs(keys)) {
      const id = bySlug.get(slug);
      if (id) {
        listOf(found, id).push(as);
        continue;
      }
      const names = unknownOf(slug)[kind];
      if (!names.includes(name)) names.push(name);
    }
    return found;
  };

  const templates = new Map<string, TemplateUse[]>();
  if (reads.templates !== "failed") {
    for (const t of [...reads.templates].sort(byName)) {
      for (const [id, as] of presetsNamedBy(t.templateJson, t.name, "templates")) {
        listOf(templates, id).push({ id: t.id, name: t.name, as });
      }
    }
  }
  const repeats = new Map<string, RepeatUse[]>();
  if (reads.repeats !== "failed") {
    for (const r of [...reads.repeats].sort(byName)) {
      for (const [id, as] of presetsNamedBy(r.eventTemplate.templateJson, r.name, "repeats")) {
        listOf(repeats, id).push({ id: r.id, name: r.name, template: r.eventTemplate.name, as });
      }
    }
  }

  const byPreset = new Map<string, PresetUses>();
  for (const p of presets) {
    byPreset.set(p.id, {
      rooms: reads.places === "failed" ? "failed" : (rooms.get(p.id) ?? []),
      defaults: reads.places === "failed" ? "failed" : (defaults.get(p.id) ?? []),
      templates: reads.templates === "failed" ? "failed" : (templates.get(p.id) ?? []),
      repeats: reads.repeats === "failed" ? "failed" : (repeats.get(p.id) ?? []),
    });
  }
  return {
    byPreset,
    unknownSlugs: [...unknown.values()].sort((a, b) => a.slug.localeCompare(b.slug)),
    tablesNotListed,
    roomsOnOtherSettings,
    defaultsOnOtherSettings,
    jsonUnread,
  };
}

/** What a preset used nowhere says. */
export const USED_NOWHERE = "Not used anywhere we can see.";

/** True when every list read and all are empty. */
export function usedNowhere(uses: PresetUses): boolean {
  return [uses.rooms, uses.defaults, uses.templates, uses.repeats].every((list) => list !== "failed" && list.length === 0);
}

/** The line where coming events would be: they are not read on this page. */
export const COMING_EVENTS_LINE = "Coming events: see each place's Schedule.";

/** The honest line about rooms the places read does not list. */
export const ROOMS_NOT_LISTED_LINE = "Closed rooms and event tables are not listed: the places read lists open rooms only.";

/** The honest line about presets the presets read leaves out. */
export const ARCHIVED_LINE = "Archived presets are not listed.";

/** A preset's uses in one line: "2 rooms · 1 place default · 1 template";
 *  {@link USED_NOWHERE} when every list read and all are empty; the kinds
 *  that did not read named when none of the others found anything. */
export function usesSummary(uses: PresetUses): string {
  const kinds: [Uses<unknown>, string, string][] = [
    [uses.rooms, "room", "rooms"],
    [uses.defaults, "place default", "place defaults"],
    [uses.templates, "template", "templates"],
    [uses.repeats, "repeat", "repeats"],
  ];
  const found = kinds
    .filter(([list]) => list !== "failed" && list.length > 0)
    .map(([list, one, many]) => {
      const n = (list as unknown[]).length;
      return `${n} ${plural(n, one, many)}`;
    });
  const failed = kinds.filter(([list]) => list === "failed").map(([, , many]) => many);
  if (found.length > 0) return found.join(" · ") + (failed.length > 0 ? ` (${failed.join(", ")} did not read)` : "");
  if (usedNowhere(uses)) return USED_NOWHERE;
  return `None found; ${failed.join(", ")} did not read.`;
}

/** The quiet notes under the where-used lists; empty when there is nothing to say. */
export function whereUsedNotes(w: WhereUsed): string[] {
  const n = (count: number, one: string, many = `${one}s`) => `${count} ${plural(count, one, many)}`;
  return [
    w.tablesNotListed > 0 &&
      `${n(w.tablesNotListed, "open table")} past the 50 listed per place ${w.tablesNotListed === 1 ? "is" : "are"} not counted.`,
    w.roomsOnOtherSettings > 0 &&
      `${n(w.roomsOnOtherSettings, "room")} ${w.roomsOnOtherSettings === 1 ? "runs" : "run"} settings not listed here (an archived preset, or not a preset).`,
    w.defaultsOnOtherSettings > 0 &&
      `${n(w.defaultsOnOtherSettings, "place")} ${w.defaultsOnOtherSettings === 1 ? "starts its" : "start their"} new tables on settings not listed here.`,
    w.jsonUnread > 0 &&
      `${n(w.jsonUnread, "template or repeat", "templates or repeats")} could not be read, so the presets ${w.jsonUnread === 1 ? "it names are" : "they name are"} not known.`,
  ].filter((note): note is string => !!note);
}

/** Which of a template's presets a use is, in words: "tables", "lobby", "tables and lobby". */
export function asLabel(as: readonly PresetRole[]): string {
  return as.join(" and ");
}

/** The page's four reads (`presets-api.ts`, `place-schedule-api.ts`, with
 *  the viewer bound), injected so {@link readPresetsPage} is testable. */
export interface PresetsApi {
  presets(): Promise<PresetData[]>;
  places(): Promise<PlaceWithRoomsData[]>;
  templates(): Promise<EventTemplateData[]>;
  repeats(): Promise<ScheduledEventData[]>;
}

/** What the page draws: the presets (the grid) and where each is used. */
export interface PresetsPageData {
  presets: PresetData[];
  whereUsed: WhereUsed;
}

/** Read the page: the four reads side by side. The presets failing fails the
 *  page; any other failing leaves its where-used lists said as not read. */
export async function readPresetsPage(api: PresetsApi): Promise<PresetsPageData> {
  const settle = <T>(read: Promise<T>): Promise<T | "failed"> => read.catch(() => "failed" as const);
  const [presets, places, templates, repeats] = await Promise.all([
    api.presets(),
    settle(api.places()),
    settle(api.templates()),
    settle(api.repeats()),
  ]);
  return { presets, whereUsed: whereUsedOf(presets, { places, templates, repeats }) };
}
